import { useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Copy,
  Check,
  HelpCircle,
} from "lucide-react";
import { Session } from "@paywithglide/glide-js";
import { getETA } from "../lib/getETAWarning";
import { formatShortWalletAddress } from "../lib/formatShortWalletAddress";
import { formatFeeAmount } from "../lib/formatFeeAmount";
import { logger } from "../lib/logger";
import { motion, AnimatePresence } from "framer-motion";
import { animations } from "../lib/animations";

type Props = {
  session: Session;
  recipient?: string;
  showRecipient?: boolean;
  showFees?: boolean;
  onNavigateToGasRefillExplainer?: () => void;
  className?: string;
};

export default function SessionSummary({
  session,
  recipient,
  showRecipient = false,
  showFees = true,
  onNavigateToGasRefillExplainer,
  className,
}: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 3000);
    } catch (error) {
      logger.error(`Failed to copy to clipboard: ${error}`);
    }
  };

  // Don't render fees section if showFees is false
  if (!showFees) {
    return null;
  }

  return (
    <div className={className}>
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="flex items-center justify-between w-full text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors text-xs h-7"
      >
        <span>
          Fee $
          {Number(session.totalFeeUSD).toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}{" "}
          •{" "}
          <span
            className={`
              transition-colors
              ${
                session.etaInSeconds > 10
                  ? "text-[--color-alert-warning-accent]"
                  : ""
              }
            `}
          >
            ETA {getETA(session.etaInSeconds)}
          </span>
          {showRecipient && recipient
            ? ` • ${formatShortWalletAddress(recipient)}`
            : ""}
        </span>
        {showDetails ? (
          <ChevronDown className="h-4 w-4 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-4 w-4 flex-shrink-0" />
        )}
      </button>

      {showDetails && (
        <div className="mt-1">
          {Number(session.gasRefuelUSD) > 0 ? (
            <div className="flex justify-between items-center relative h-7">
              <div className="text-xs text-[--color-text-secondary] flex items-center gap-1">
                Gas Refill
                <button
                  type="button"
                  className="focus:outline-none flex items-center justify-center"
                  onClick={() =>
                    onNavigateToGasRefillExplainer &&
                    onNavigateToGasRefillExplainer()
                  }
                  aria-label="Gas Refill explanation"
                >
                  <HelpCircle
                    className="w-3.5 h-3.5 text-[--color-text-secondary] hover:text-[--color-text-primary] transition-colors"
                    strokeWidth={2}
                  />
                </button>
              </div>
              <div className="text-xs text-[--color-text-primary]">
                $
                {Number(session.gasRefuelUSD).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </div>
            </div>
          ) : null}

          <div className="flex justify-between items-center h-7">
            <div className="text-xs text-[--color-text-secondary]">
              Service Fee
            </div>
            <div className="text-xs text-[--color-text-primary]">
              {formatFeeAmount(session.serviceFeeUSD)}
            </div>
          </div>

          <div className="flex justify-between items-center h-7">
            <div className="text-xs text-[--color-text-secondary]">Gas Fee</div>
            <div className="text-xs text-[--color-text-primary]">
              {formatFeeAmount(session.gasFeeUSD)}
            </div>
          </div>

          <div className="flex justify-between items-center h-7">
            <div className="text-xs text-[--color-text-secondary]">
              Payment Gas Fee
            </div>
            <div className="text-xs text-[--color-text-primary]">
              {formatFeeAmount(session.paymentTransactionGasFeeUSD)}
            </div>
          </div>

          <div className="flex justify-between items-center h-7">
            <div className="text-xs text-[--color-text-secondary]">ETA</div>
            <div className="text-xs text-[--color-text-primary]">
              {getETA(session.etaInSeconds)}
            </div>
          </div>

          {showRecipient && recipient ? (
            <div className="flex justify-between items-center h-7">
              <div className="text-xs text-[--color-text-secondary]">
                Recipient
              </div>
              <div className="flex items-center gap-1">
                <motion.button
                  type="button"
                  whileTap={{ scale: 0.9 }}
                  transition={animations.spring.medium}
                  className="p-1 hover:bg-[--color-background-secondary] rounded transition-colors"
                  onClick={() => copyToClipboard(recipient)}
                  aria-label="Copy recipient address"
                >
                  <AnimatePresence mode="wait">
                    {isCopied ? (
                      <motion.div
                        key="check"
                        initial={animations.scaleRotate.initial}
                        animate={animations.scaleRotate.animate}
                        exit={animations.scaleRotate.exit}
                        transition={animations.spring.bouncy}
                      >
                        <Check
                          className="w-4 h-4 text-green-500"
                          strokeWidth={2}
                        />
                      </motion.div>
                    ) : (
                      <motion.div
                        key="copy"
                        initial={{ scale: 1 }}
                        exit={{ scale: 0, rotate: 180 }}
                        transition={animations.spring.stiff}
                      >
                        <Copy
                          className="w-4 h-4 text-[--color-text-secondary]"
                          strokeWidth={2}
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.button>
                <div className="text-xs text-[--color-text-primary]">
                  {formatShortWalletAddress(recipient)}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
